import React, { useEffect, useEffectEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  ArrowRightLeft,
  BookOpen,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Loader2,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TransactionPagination } from '@/components/general-ledger/TransactionPagination';
import {
  useFundTransactions,
  useGenerateManagementFeeDrafts,
} from '@/hooks/react-query/useFundTransactions';
import {
  FUND_TRANSACTION_TYPE_LABELS,
  isBooked,
  type FundTransactionStatus,
  type FundTransactionSummary,
  type FundTransactionType,
} from '@/types/fund-transactions';
import { formatCurrency, formatDate } from '@/utils/formatters';
import { FundTransactionStatusBadge } from './FundTransactionStatusBadge';

interface FundTransactionsListProps {
  fundId: string;
}

// Matches the backend default page size for this endpoint.
const PAGE_SIZE = 50;

// Sentinel option that groups every asset transaction type behind the
// `transaction_category=ASSET` backend filter instead of a single type.
const ASSET_GROUP = 'ASSET_GROUP' as const;

type TypeFilterValue = 'ALL' | typeof ASSET_GROUP | FundTransactionType;

const TYPE_FILTER_OPTIONS: ReadonlyArray<{
  value: TypeFilterValue;
  label: string;
}> = [
  { value: 'ALL', label: 'All types' },
  { value: ASSET_GROUP, label: 'All Asset Transactions' },
  { value: 'DRAWDOWN', label: 'Drawdown' },
  { value: 'MANAGEMENT_FEE', label: 'Management Fee' },
  { value: 'MANAGEMENT_FEE_RECOGNITION', label: 'Management Fee Recognition' },
  { value: 'FUND_COST', label: 'Fund Cost' },
  { value: 'FUND_COST_RECOGNITION', label: 'Fund Cost Recognition' },
  { value: 'DEAL_COST', label: 'Deal Cost' },
  { value: 'DISTRIBUTION', label: 'Distribution' },
  { value: 'ASSET_SALE', label: 'Asset Sale' },
  { value: 'INVESTMENT_PURCHASE', label: 'Investment Purchase' },
  { value: 'FINANCING_ROUND', label: 'Financing Round' },
  { value: 'CONVERTIBLE_INVESTMENT', label: 'Convertible Investment' },
  { value: 'CONVERTIBLE_CONVERSION', label: 'Convertible Conversion' },
  { value: 'CONVERTIBLE_REPAYMENT', label: 'Convertible Repayment' },
  { value: 'CURRENCY_CONVERSION', label: 'Currency Conversion' },
  { value: 'FX_PERIOD_END', label: 'FX Period End' },
];

const STATUS_FILTER_OPTIONS: ReadonlyArray<{
  value: 'ALL' | FundTransactionStatus;
  label: string;
}> = [
  { value: 'ALL', label: 'All statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ISSUED', label: 'Issued' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'POSTED', label: 'Posted' },
  { value: 'REVERSED', label: 'Reversed' },
];

export function FundTransactionsList({ fundId }: FundTransactionsListProps) {
  const navigate = useNavigate();

  // Filters live in the URL query string so the table view is deep-linkable
  // and survives back/forward. Defaults are omitted to keep URLs clean.
  const [searchParams, setSearchParams] = useSearchParams();

  const typeFilter = (searchParams.get('txn_type') ?? 'ALL') as TypeFilterValue;
  const statusFilter = (searchParams.get('txn_status') ?? 'ALL') as
    'ALL' | FundTransactionStatus;
  const dateFrom = searchParams.get('txn_from') ?? '';
  const dateTo = searchParams.get('txn_to') ?? '';
  const sortOrder: 'asc' | 'desc' =
    searchParams.get('txn_sort') === 'asc' ? 'asc' : 'desc';
  const page = Number(searchParams.get('txn_page')) || 1;

  // Changing any filter or the sort order invalidates the current offset, so
  // every filter write drops the page back to the first one.
  const setFilterParam = (key: string, value: string, fallback: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === fallback) next.delete(key);
        else next.set(key, value);
        next.delete('txn_page');
        return next;
      },
      { replace: true }
    );
  };

  const setPage = (nextPage: number) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (nextPage <= 1) next.delete('txn_page');
        else next.set('txn_page', String(nextPage));
        return next;
      },
      { replace: true }
    );
  };

  const isAssetGroup = typeFilter === ASSET_GROUP;
  const filters = {
    transaction_type:
      typeFilter === 'ALL' || isAssetGroup ? undefined : typeFilter,
    transaction_category: isAssetGroup ? ('ASSET' as const) : undefined,
    status: statusFilter === 'ALL' ? undefined : statusFilter,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    sort_order: sortOrder,
    page,
    page_size: PAGE_SIZE,
  };

  const { mutate: generateDrafts, isPending: isGenerating } =
    useGenerateManagementFeeDrafts(fundId);
  const { data, isLoading } = useFundTransactions(fundId, filters);
  const rows: FundTransactionSummary[] = data?.items ?? [];
  const totalCount = data?.total_count ?? 0;

  const onFundOpened = useEffectEvent(() => {
    generateDrafts();
  });

  useEffect(() => {
    if (!fundId) return;
    onFundOpened();
  }, [fundId]);

  const handleRowClick = (row: FundTransactionSummary) => {
    if (row.transaction_type === 'DRAWDOWN' && row.drawdown_id) {
      navigate(`/app/funds/${fundId}/drawdowns/${row.drawdown_id}/preview`);
      return;
    }
    navigate(`/app/funds/${fundId}/fund-transactions/${row.id}`);
  };

  const handleOpenLedgerEntry = (
    event: React.MouseEvent,
    row: FundTransactionSummary
  ) => {
    event.stopPropagation();
    navigate(
      `/app/funds/${fundId}?tab=general-ledger&section=entries&reference_id=${row.id}`
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <Select
                value={typeFilter}
                onValueChange={(value) =>
                  setFilterParam('txn_type', value, 'ALL')
                }
              >
                <SelectTrigger className="w-[180px]" data-testid="type-filter">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_FILTER_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={statusFilter}
                onValueChange={(value) =>
                  setFilterParam('txn_status', value, 'ALL')
                }
              >
                <SelectTrigger
                  className="w-[180px]"
                  data-testid="status-filter"
                >
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_FILTER_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1">
                <Label
                  htmlFor="date-from"
                  className="text-sm text-muted-foreground sr-only"
                >
                  From
                </Label>
                <Input
                  id="date-from"
                  type="date"
                  value={dateFrom}
                  onChange={(e) =>
                    setFilterParam('txn_from', e.target.value, '')
                  }
                  className="w-[150px]"
                  placeholder="From"
                  data-testid="date-from-filter"
                />
              </div>
              <div className="flex items-center gap-1">
                <Label
                  htmlFor="date-to"
                  className="text-sm text-muted-foreground sr-only"
                >
                  To
                </Label>
                <Input
                  id="date-to"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setFilterParam('txn_to', e.target.value, '')}
                  className="w-[150px]"
                  placeholder="To"
                  data-testid="date-to-filter"
                />
              </div>
            </div>
          </div>
          {isGenerating && (
            <div className="mt-2 flex items-center text-sm text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin mr-2" />
              Generating drafts…
            </div>
          )}
        </CardHeader>
        <CardContent>
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}
          {!isLoading && rows.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <ArrowRightLeft className="h-12 w-12 mx-auto mb-4 text-muted-foreground/40" />
              <p>No fund transactions found</p>
              <p className="text-sm">
                Drafts materialise automatically from active schedules.
              </p>
            </div>
          )}
          {!isLoading && rows.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <button
                        type="button"
                        onClick={() =>
                          setFilterParam(
                            'txn_sort',
                            sortOrder === 'desc' ? 'asc' : 'desc',
                            'desc'
                          )
                        }
                        className="flex items-center gap-1 hover:text-foreground"
                      >
                        Date
                        {sortOrder === 'desc' ? (
                          <ChevronDown className="h-3 w-3" />
                        ) : (
                          <ChevronUp className="h-3 w-3" />
                        )}
                      </button>
                    </TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow
                      key={row.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleRowClick(row)}
                      data-testid={`fund-transaction-row-${row.id}`}
                    >
                      <TableCell className="text-muted-foreground">
                        {formatDate(row.transaction_date)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className="bg-muted text-muted-foreground border-border"
                        >
                          {FUND_TRANSACTION_TYPE_LABELS[row.transaction_type] ??
                            row.transaction_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {row.transaction_type === 'DISTRIBUTION' &&
                        row.distribution_number != null ? (
                          <span className="font-medium">
                            Distribution #{row.distribution_number}
                          </span>
                        ) : (
                          <span className="font-medium">
                            {row.description ?? '-'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatCurrency(row.amount, {
                          currency: row.currency,
                        })}
                      </TableCell>
                      <TableCell>
                        <FundTransactionStatusBadge status={row.status} />
                      </TableCell>
                      <TableCell>
                        {isBooked(row.status) ? (
                          <button
                            type="button"
                            onClick={(event) =>
                              handleOpenLedgerEntry(event, row)
                            }
                            className="text-muted-foreground hover:text-foreground"
                            title="View ledger entry"
                            data-testid={`ledger-entry-link-${row.id}`}
                          >
                            <BookOpen className="h-4 w-4" />
                          </button>
                        ) : (
                          <ExternalLink className="h-4 w-4 text-muted-foreground" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {/* Sits outside the empty-state branch so a stale out-of-range page
              still offers a way back instead of stranding the user. */}
          {!isLoading && totalCount > PAGE_SIZE && (
            <div className="mt-4">
              <TransactionPagination
                page={page}
                pageSize={PAGE_SIZE}
                totalCount={totalCount}
                onPageChange={setPage}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
